import React, { useContext, useEffect } from 'react';
import { AppContext } from 'src/contexts/AppContext';
import { BookingProvider } from 'src/contexts/BookingFlowContext';
import { ReservationOrder } from 'src/entities/company';
import { ICommonModuleProps } from '../../models/modules';
import getStepOrder, { mapReservationOrder } from '../../services/getStepOrder';
import ReservationEnd from './ReservationEnd/ReservationEnd';
import { createReservationEndUser } from './ReservationEnd/functions/createFunctions';
import Stepper from './Stepper/Stepper';
import { Routes } from 'src/routes';
import { useLocation } from 'react-router-dom';
import { useBookingFlowReducer } from './BookingFlowReducer';

export interface IBookingFlowProps extends ICommonModuleProps {
    shouldHideInitialProgressBar?: boolean;
    reservationOrder: ReservationOrder;
}

const BookingFlow: React.FC<IBookingFlowProps> = (props) => {
    const { shouldHideInitialProgressBar, reservationOrder } = props;
    const context = useContext(AppContext);
    const location = useLocation();
    const stepOrderType = mapReservationOrder(reservationOrder);
    const { state } = useBookingFlowReducer(stepOrderType);
    const { reservations, activeReservationIndex, stepOrderTypes, activeStepIndex, actions } =
        state;
    const { setReservationEndUser, addNewReservation, removeReservation, setActiveStepIndex } =
        actions;
    const stepOrder = getStepOrder(stepOrderTypes[activeReservationIndex]);

    const handleActiveStepIndexChange = (index: number) => {
        setActiveStepIndex(index);
    };

    const handleEndStep = () => {
        setActiveStepIndex(stepOrder.length - 1);
    };

    useEffect(() => {
        setReservationEndUser(createReservationEndUser(context.user));
    }, []);

    useEffect(() => {
        if (reservations.length === 0) {
            addNewReservation();
        }
    }, [reservations]);

    useEffect(() => {
        if (location.state?.newReservation) {
            reservations.forEach(() => removeReservation());
        }
    }, [location]);

    return (
        <section className="booking-flow">
            <BookingProvider value={{ ...state, ...props }}>
                {reservations.map((_, index) => (
                    <Stepper
                        key={`stepper-${index}`}
                        isActive={
                            activeStepIndex < stepOrder.length - 1 &&
                            index === activeReservationIndex
                        }
                        activeStepIndex={activeStepIndex}
                        stepOrder={getStepOrder(stepOrderTypes[index])}
                        pendingLength={reservations.length}
                        removeReservation={removeReservation}
                        onChange={handleActiveStepIndexChange}
                        shouldHideInitialProgressBar={shouldHideInitialProgressBar && index === 0}
                    />
                ))}
                {location.pathname === Routes.ReservationEnd && (
                    <ReservationEnd
                        index={stepOrder.length - 1}
                        isEndStep={activeStepIndex === stepOrder.length - 1}
                        onEndStep={handleEndStep}
                        reservationOrder={reservationOrder}
                    />
                )}
            </BookingProvider>
        </section>
    );
};

export default BookingFlow;
